import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { PlexEnvironment } from '@app/env';
import { NameValueInt } from '@app/shared/models/name-value';
import { IProjectChange } from '@app/shared/models/project/project-change';
import { ProjectsView } from '@app/shared/models/projects-view';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { DataResult, State, toODataString } from '@progress/kendo-data-query';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { catchError, map, skip, switchMap, takeUntil, tap } from 'rxjs/operators';

export interface HttpRequestState<T> {
  isLoading: boolean;
  value?: T;
  error?: HttpErrorResponse | Error;
}

@Injectable({
  providedIn: 'root'
})
export class ProjectGridViewService extends BehaviorSubject<GridDataResult> implements OnDestroy {
  private http = inject(HttpClient);
  public environment = inject(PlexEnvironment);

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  protected destroy$: Subject<boolean> = new Subject();
  protected httpRequestState$ = new BehaviorSubject<HttpRequestState<'EMPTY' | 'FETCHING' | 'FETCHED' | 'ERROR'>>({ value: 'EMPTY', isLoading: false });
  protected triggerFetch$ = new BehaviorSubject<string>('');

  constructor() {
    super(null);

    this.triggerFetch$.pipe(
      skip(1), // we don't want an initial fetch on component creation
      takeUntil(this.destroy$),
      tap(() => {
        this.cancelAllRequests(false);
        this.httpRequestState$.next({ value: 'FETCHING', isLoading: true })
      }),
      switchMap((url) => {
        return this.http.get<GridDataResult>(url)
          .pipe(
            catchError((errorResponse: HttpErrorResponse) => {
              this.httpRequestState$.next({ value: 'ERROR', isLoading: false, error: errorResponse });
              return null;
            }),
            map((response: any) => {
              return <GridDataResult>{
                data: response.value,
                total: parseInt(response['@odata.count'], 10)
              };
            })
          );
      }))
      .subscribe((response: GridDataResult) => {

        super.next(response);
        this.httpRequestState$.next({ value: 'FETCHED', isLoading: false });
      });
  }

  private BASE_URL = this.environment.apiUrl;

  public select: string = 'ProjectId,AfeNumber,Name,FunctionId,Function,AssetTeamId,AssetTeamCode,ProjectTypeId,ProjectType,ProjectSubTypeId,ProjectSubType,ProjectStatus,ProjectStatusId,IsProjectTeamSync,ScenarioId,DefaultProjectTeam,DefaultProjectTeamId,CreatedBy,DivisionId,Division,StartDate,NeedByDate,AfeApprovalDate,ProjectManagerId,ProjectManagerName,ProjectControlsAnalystId,ProjectControlsAnalystName,ProjectEngineerId,ProjectEngineerName,ProgramManagerId,ProgramManagerName,ProjectTechId,ProjectTechName,CostCenter,BudgetCategoryId,BudgetCategoryName,CapitalOperationalExpendatureId,CapitalOperationalExpendature,AfeApprovalTeam,WBSType,WBSSubType,WBSRecordStatus';
  private tableName: string = 'ProjectsView?$select=';

  public projectsById: Map<number, any> = new Map();

  public query(state: any): Observable<GridDataResult> {
    return this.fetch(this.tableName + this.select, state);
  }

  public UpdateFromNotification(change: IProjectChange, projectStatuses: NameValueInt[]): void {
    this.UpdateOneFromNotification(change, null, projectStatuses);
  }

  public UpdateOneFromNotification(change: IProjectChange, project: any, projectStatuses: NameValueInt[]): void {
    //find the project, change hte project
    let grdData = this.value as GridDataResult;
    let projects = grdData.data as ProjectsView[];
    if (!projects || !projects.length) {
      return;
    }
    let ps = projectStatuses.find(x => x.value == change.ProjectStatusId);

    if (!ps) return;

    let one = projects.find(x => x.ProjectId === change.ProjectId);

    if (one && one.ProjectStatusId !== change.ProjectStatusId) {
      if (ps) {
        one.ProjectStatus = ps.name;
        one.ProjectStatusId = ps.value;
      }
    }
    if (one != project && project && project.ProjectStatus != ps.name) {
      one.ProjectStatus = ps.name;
      one.ProjectStatusId = ps.value;
    }
  }

  public state: State = {
    skip: 0,
    take: 5,
    sort: [{ dir: "desc", field: "ProjectId" }]
  };

  cancelAllRequests(updateState = true) {
    if (!this.subscriptions) {
      return;
    }
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions = [];
  }

  private subscriptions: Subscription[];

  private fetch(tableName: string, state: any): Observable<GridDataResult> {
    const queryStr = `&${toODataString(state)}&$count=true`;
    // for ProjectId need to replace / with . for Integer partial matching
    let newStr = queryStr.replace('Edm/String', 'Edm.String');

    let path = `${this.BASE_URL}${tableName}${newStr}`;
    this.triggerFetch$.next(path);
    return this;
  }

  public allData = (state: State): Observable<DataResult> => {
    let newState: State = {};
    Object.assign(newState, state);

    delete newState.skip;
    delete newState.take;

    return this.fetch(this.tableName + this.select, newState);
  }

  public getProjectData = (projectId: number): Promise<any> => {
    let select: string = `$select=ProjectId,AfeNumber,Name,FunctionId,Function,AssetTeamId,AssetTeamCode,ProjectTypeId,ProjectType,ProjectSubTypeId,ProjectSubType,ProjectStatus,ProjectStatusId,IsProjectTeamSync,ScenarioId,DefaultProjectTeam,DefaultProjectTeamId,CreatedBy,DivisionId,Division,StartDate,NeedByDate,AfeApprovalDate,ProjectManagerId,ProjectManagerName,ProjectControlsAnalystId,ProjectControlsAnalystName,ProjectEngineerId,ProjectEngineerName,ProgramManagerId,ProgramManagerName,ProjectTechId,ProjectTechName,CostCenter,BudgetCategoryId,BudgetCategoryName,CapitalOperationalExpendatureId,CapitalOperationalExpendature,AfeApprovalTeam`;
    let filter: string = `&$filter=AssetTeamId eq 3 and ProjectId eq ${projectId}`;
    let count: string = `&$count=true`;
    let tableName: string = `ProjectsView?${select}${filter}${count}`;

    let url = `${this.BASE_URL}${tableName}`;
    return this.http.get(url).toPromise().then(response => response);
  }
}